// TeamManagement.jsx - Enhanced version with inline notifications
import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';

// Import sub-components
import CreateTeamForm from './CreateTeamForm';
import MembersTable from './MembersTable';
import SglContext from '../../SglContext';
import { InviteMemberModal, EditMemberModal } from './InviteEditMemberModal';
import { LoadingSpinner, ErrorDisplay } from './utilities';
import { useLocation } from 'react-router-dom';

// Import styles
import './TeamManagement.css';

// Inline Notification Component
const InlineNotification = ({ notification, onDismiss }) => {
  if (!notification) return null;

  const getNotificationStyles = () => {
    const typeStyles = {
      success: {
        backgroundColor: '#fdfffe',
        borderColor: '#059669',
        color: '#059669'
      },
      error: {
        backgroundColor: '#fef2f2',
        borderColor: '#dc2626',
        color: '#dc2626'
      },
      info: {
        backgroundColor: '#f0f9ff',
        borderColor: '#0369a1',
        color: '#0369a1'
      },
      warning: {
        backgroundColor: '#fffbeb',
        borderColor: '#d97706',
        color: '#d97706'
      }
    };

    return typeStyles[notification.type] || typeStyles.info;
  };

  const getIcon = () => {
    switch (notification.type) {
      case 'success': return '✓';
      case 'error': return '';
      case 'warning': return '⚠';
      case 'info': return 'ℹ';
      default: return 'ℹ';
    }
  };

  return (
    <div 
      className="inline-notification"
      style={{
        ...getNotificationStyles(),
        padding: '8px 12px',
        margin: '0px 0px 10px 0px',
        borderRadius: '6px',
        border: '1px solid',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '4px',
        fontSize: '14px',
        fontWeight: '500'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 1 }}>
        <span style={{ fontSize: '16px', lineHeight: '1' }}>
          {getIcon()}
        </span>
        <span style={{ lineHeight: '1.4' }}>
          {notification.message}
        </span>
      </div>
      {onDismiss && (
        <button
          onClick={onDismiss}
          style={{
            background: 'none',
            border: 'none',
            color: 'inherit',
            fontSize: '18px',
            cursor: 'pointer',
            padding: '10px',
            lineHeight: '1',
            boxSizing: 'border-box',
            marginTop: '-5px'
          }}
        >
          ×
        </button>
      )}
    </div>
  );
};

// Notification Manager Hook
const useInlineNotifications = () => {
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = 'info', duration = 5000) => {
    const notificationObj = { message, type, timestamp: Date.now() };
    setNotification(notificationObj);
    
    if (duration > 0) {
      setTimeout(() => {
        setNotification(prev => 
          prev && prev.timestamp === notificationObj.timestamp ? null : prev
        );
      }, duration);
    }
    
    return notificationObj.timestamp;
  };

  const dismissNotification = () => {
    setNotification(null);
  };

  const showSuccess = (message, duration = 5000) => showNotification(message, 'success', duration);
  const showError = (message, duration = 6000) => showNotification(message, 'error', duration);
  const showInfo = (message, duration = 4000) => showNotification(message, 'info', duration);
  const showWarning = (message, duration = 5000) => showNotification(message, 'warning', duration);

  return {
    notification,
    showNotification,
    showSuccess,
    showError,
    showInfo,
    showWarning,
    dismissNotification
  };
};

const TeamManagement = () => {
  const [allTeamsData, setAllTeamsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentTeamId, setCurrentTeamId] = useState(null);

  // Modal states
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  // Loading states
  const [isCreatingTeam, setIsCreatingTeam] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  
  const location = useLocation();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const userOwnsTeam = allTeamsData.some(team => team.isOwner === true);

  const [isDeleting, setIsDeleting] = useState(false);

  const Sgl = useContext(SglContext);
  const { 
    notification, 
    showSuccess, 
    showError, 
    showInfo, 
    showWarning, 
    dismissNotification 
  } = useInlineNotifications();

  // Load data on component mount
  useEffect(() => {
    loadUserTeams();
    loadAvailableRoles();
  }, []);

  useEffect(() => {
    loadUserTeams();
  }, [Sgl?.userTeams]); 

  useEffect(() => {
    if (location.state?.showCreateForm) {
      setShowCreateForm(true);
    }
  }, [location.state]);

  const loadUserTeams = async (showLoadingNotification = false) => {
    try {
      setLoading(true);
      setError('');
      
      if (showLoadingNotification) {
        showInfo('Refreshing team data...', 2000);
      }
      
      const url = `${Sgl.nestApiUrl}/trainer-teams/user-teams`;
      const response = await axios.get(url);
      
      // Response is now array of teams directly
      setAllTeamsData(response.data);
      
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load team data';
      setError(errorMessage);
      showError(errorMessage, 8000);
      console.error('Error loading team context:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableRoles = async () => {
    try {
      const response = await axios.get(`${Sgl.nestApiUrl}/trainer-teams/roles`);
      setAvailableRoles(response.data);
    } catch (err) {
      console.error('Error loading roles:', err);
      showError('Failed to load available roles', 6000);
    }
  };

  const handleTeamSwitch = (teamId) => {
    // Clear notifications when switching contexts
    dismissNotification();
    
    if (teamId === 'create') {
      setCurrentTeamId('create');
    } else {
      setCurrentTeamId(teamId);
    }
  };

  const handleCreateTeam = async (formData) => {
    try {
      setIsCreatingTeam(true);
      showInfo('Creating team...', 3000);
      
      await axios.post(`${Sgl.nestApiUrl}/trainer-teams`, formData);

      setCurrentTeamId(null);
      await loadUserTeams(false); // Don't show loading notification during refresh after create
      if (Sgl.getUser) {
        Sgl.getUser();
      }
      setShowCreateForm(false);
      showSuccess(`Team "${formData.name}" created successfully!`, 6000);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to create team';
      showError(errorMessage, 8000);
    } finally {
      setIsCreatingTeam(false);
    }
  };

  const handleInviteMember = async (inviteForm) => {
    try {
      showInfo('Sending invitation...', 3000);
      
      await axios.post(`${Sgl.nestApiUrl}/trainer-teams/invite`, inviteForm);
      await loadUserTeams(false);
      setIsInviteModalOpen(false);
      
      showSuccess(`Invitation sent to ${inviteForm.email} successfully!`, 6000);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to send invitation';
      showError(errorMessage, 8000);
    }
  };

  const handleEditMember = (member) => {
    setSelectedMember(member);
    setIsEditModalOpen(true);
  };

  const handleUpdateMember = async (memberData) => {
    try {
      showInfo('Updating member...', 3000);
      
      await axios.put(`${Sgl.nestApiUrl}/trainer-teams/member/${memberData.sgl_id}`, {
        roleId: memberData.roleId,
        status: memberData.status,
        email_notification: memberData.email_notification,
      });

      await loadUserTeams(false);
      setIsEditModalOpen(false);
      setSelectedMember(null);
      
      showSuccess(`Member ${memberData.name || memberData.email} updated successfully!`, 6000);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update member';
      showError(errorMessage, 8000);
    }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    if (!window.confirm(`Are you sure you want to remove ${memberName || 'this team member'}?`)) {
      return;
    }

    try {
      showInfo('Removing member...', 3000);
      
      await axios.delete(`${Sgl.nestApiUrl}/trainer-teams/member/${memberId}`);
      await loadUserTeams(false);
      
      showSuccess(`${memberName || 'Member'} removed successfully!`, 6000);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to remove member';
      showError(errorMessage, 8000);
    }
  };

  const handleCancelInvite = async (requestId, inviteEmail) => {
    if (!window.confirm(`Are you sure you want to cancel the invitation to ${inviteEmail || 'this user'}?`)) {
      return;
    }

    try {
      showInfo('Cancelling invitation...', 3000);
      
      await axios.delete(`${Sgl.nestApiUrl}/trainer-teams/invitation/${requestId}`);
      await loadUserTeams(false);
      
      showSuccess(`Invitation to ${inviteEmail || 'user'} cancelled successfully!`, 6000);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to cancel invitation';
      showError(errorMessage, 8000);
    }
  };

  const handleDeleteTeam = async (teamId) => {
    const team = allTeamsData.find(t => t.teamId === teamId);
    const teamName = team ? team.name : 'this team';

    if (!window.confirm(`Are you sure you want to delete the team ${teamName}?`)) {
      return;
    }

    try {
      setIsDeleting(true);
      showInfo('Deleting team...', 3000);
      await axios.delete(`${Sgl.nestApiUrl}/trainer-teams/delete-team/${teamId}`);
      setCurrentTeamId(null);
      await loadUserTeams(false);
      showSuccess(`Team "${teamName}" deleted successfully!`, 6000);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to delete team';
      showError(errorMessage, 8000);
    }
    finally {
      setIsDeleting(false);
    }
  };

  // Loading and error states
  if (loading) {
    return (
      <div className="team-management">
        <LoadingSpinner message="Loading team data..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="team-management">
        <ErrorDisplay error={error} onRetry={() => loadUserTeams(true)} />
      </div>
    );
  }

  // Show create team interface if no teams exist
  if ((!allTeamsData || allTeamsData.length === 0) || (!userOwnsTeam && showCreateForm)) {
    return (
      <div className="team-management">
        {/* Notifications for create team context */}
        <InlineNotification 
          notification={notification} 
          onDismiss={dismissNotification} 
        />
        
        <CreateTeamForm 
          teamContext={{ hasTeam: allTeamsData.length > 0, userTeams: allTeamsData }}
          onCreateTeam={handleCreateTeam}
          isCreating={isCreatingTeam}
          currentTeamId="create"
          onTeamSwitch={handleTeamSwitch}
          showNotification={showWarning}
          onCancel={allTeamsData.length > 0 ? () => setShowCreateForm(false) : undefined}
        />
      </div>
    );
  }

  return (
    <div className="team-management">
      {/* Primary notifications - appears after any team header/switcher */}
      <InlineNotification 
        notification={notification} 
        onDismiss={dismissNotification} 
      />

      {!userOwnsTeam && !showCreateForm && (
        <div style={{ marginBottom: '10px', display: "flex", justifyContent:"end" }}>
          <button
            onClick={() => setShowCreateForm(true)}
            className="new-primary-button"
            >
            CREATE MY TEAM
          </button>
        </div>
      )}

      <MembersTable 
        allTeamsData={allTeamsData}
        onEditMember={handleEditMember}
        onRemoveMember={(memberId, memberName) => handleRemoveMember(memberId, memberName)}
        onCancelInvite={(requestId, inviteEmail) => handleCancelInvite(requestId, inviteEmail)}
        onInviteMember={(teamId) => {
          setCurrentTeamId(teamId);
          setIsInviteModalOpen(true);
        }}
        onDeleteTeam={(teamId) => handleDeleteTeam(teamId)}
        isDeleting={isDeleting}
        showAllTeams={true}
      />

      <InviteMemberModal 
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onInvite={handleInviteMember}
        availableRoles={availableRoles}
        currentTeamId={currentTeamId}
        showNotification={showWarning} // Pass notification function
      />

      <EditMemberModal 
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        member={selectedMember}
        onUpdate={handleUpdateMember}
        availableRoles={availableRoles}
        showNotification={showWarning} // Pass notification function
      />

      {/* Show create team form if in create mode */}
      {currentTeamId === 'create' && (
        <div style={{marginTop: '40px', paddingTop: '40px', borderTop: '1px solid #e2e8f0'}}>
          <CreateTeamForm 
            teamContext={{ hasTeam: true, userTeams: allTeamsData }}
            onCreateTeam={handleCreateTeam}
            isCreating={isCreatingTeam}
            currentTeamId="create"
            onTeamSwitch={handleTeamSwitch}
            showNotification={showWarning} // Pass notification function
          />
        </div>
      )}
    </div>
  );
};

export default TeamManagement;